import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { App } from "antd5";
import { Flex } from "styles/utility-components";

import { providerIconColorMap, providerIconKeyMap } from "components/integrations/ProviderButtons";
import { friendlyProviderName } from "components/integrations/util";
import PaywallPopover from "components/paywall/PaywallPopover";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { appRoutes } from "lib/appRoutes";
import { ProviderTypes } from "lib/generated/integration-api";
import { ContactSearchResult } from "lib/hooks/api/contacts/useContactSearch";
import {
  useRevealContact,
  useRevealContacts,
  useViewContact,
} from "lib/hooks/api/contacts/useRevealContacts";
import { useConnectedParagonCrm } from "lib/hooks/api/integrations/useConnectedParagonCrm";
import { useDescribeContactDetails } from "lib/hooks/api/integrations/useContactDetails";
import { useDescribeMappableEntity } from "lib/hooks/api/integrations/useDescribeMappableEntity";
import { useContactCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useParagonCrmMapping } from "lib/hooks/api/integrations/useParagonCrmRecordForm";
import { useSendContact } from "lib/hooks/api/integrations/useSendContact";
import { useIntegrationApi } from "lib/integrationApiContext";
import { buildRecordForm } from "lib/integrations/paragonCrm/buildRecordForm";
import { fieldMappingSectionId } from "lib/integrations/paragonCrm/fieldCompatibility";
import { CrmRecordSeed, LookupTargets } from "lib/integrations/paragonCrm/recordFields";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventNames, useTracking } from "lib/tracking";
import { CognismContactSource, ContactTier } from "lib/types/graphQLEnums";

type RevealedPii = {
  email?: string | null;
  mobilePhoneNumbers?: (string | null)[] | null;
  directPhoneNumbers?: (string | null)[] | null;
  phoneNumbers?: (string | null)[] | null;
};

/**
 * Per-row "Send to CRM" button, source-aware for the unified contacts table. Both sources
 * reveal-on-push — an un-revealed contact is revealed first so the CRM record gets email/phone.
 * Cognism rows carry their stable Cognism contact id, from which the integrations service derives
 * the CRM object-mapping id, so the
 * same contact pushed from search or a lead maps to one CRM record.
 */
export default function SendCognismContactToCRMButton({
  contact,
}: {
  contact: ContactSearchResult;
}) {
  const { message } = App.useApp();
  const { logEvent } = useTracking();
  const navigate = useNavigate();

  const { data: providerTypeResponse } = useContactCrmProvider();
  const providerType = providerTypeResponse?.providerType;
  // A Paragon install is hidden from that hook: its push comes from the company's own field
  // mapping, which none of the provider-keyed UI below knows the field names of.
  const paragonCrm = useConnectedParagonCrm();
  // Only the mapped push reads the mapping: a native install is sent the fields by name, so it is
  // not asked for unless this row is going to use it.
  const usesMapping = !providerType && !!paragonCrm;
  const {
    mapping: contactMapping,
    isLoading: isLoadingMapping,
    error: mappingError,
  } = useParagonCrmMapping("contact", usesMapping);
  const {
    data: describedContact,
    isInitialLoading: isDescribing,
    error: describeError,
  } = useDescribeMappableEntity(
    contactMapping?.object.logicalName ?? "",
    usesMapping && !!contactMapping,
  );
  const api = useIntegrationApi();

  // Search rows carry the stable Cognism contact_id directly as `id`; Oscar rows have no Cognism id
  // (the service keys the mapping off the Oscar id instead). The integrations service derives the
  // CRM object-mapping id from these, so a contact maps to one CRM record across search and leads.
  const isCognism = contact.source === "cognism";
  const cognismContactId = isCognism ? contact.id : undefined;
  const { data: extraContactData } = useDescribeContactDetails(contact.id, cognismContactId, {
    enabled: !!providerType || !!paragonCrm,
  });

  const { checkSubscription, authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": "Cognism CRM button",
  });
  const disableContactsCta = !hasContacts;

  const { mutate: reveal, isLoading: isRevealing } = useRevealContact();
  const { mutate: revealMany, isLoading: isRevealingMany } = useRevealContacts();
  const { mutate: view, isLoading: isViewing } = useViewContact();

  // The provider whose logo and name this shows, and which every event records. Dynamics is hidden
  // from the hook above, because none of the provider-keyed UI elsewhere can render it, but it
  // identifies itself here like any other — and an event with no provider on it says nothing. The
  // fallback only ever surfaces with Dynamics connected: the guard below renders nothing when
  // neither is, and it cannot move above this because the hooks in between must run every render.
  const provider = providerType ?? paragonCrm?.providerType ?? ProviderTypes.Dynamics;

  const trackingAttributes = {
    "Connected provider": provider,
    "Data types": "Contact",
    "Contact source": isCognism ? "Cognism" : "Oscar",
    "Contact job title": contact.jobTitle,
    "Buyer name": contact.buyer?.name ?? contact.account?.name ?? contact.oscarOrgName,
  };

  const { mutate: sendContact, isLoading: isSending } = useSendContact({
    onSuccess: (data) => {
      logEvent(EventNames.objectSentToCRM, trackingAttributes);
      message.open({
        type: data.isDuplicate ? "warning" : "success",
        content: (
          <Flex gap={8} alignItems="center">
            {data.isDuplicate
              ? `Contact already exists in your ${providerName} account`
              : `Sent to ${providerName} `}

            {data.externalUrl && (
              <Button
                as="a"
                buttonType="secondary"
                href={data.externalUrl}
                target="new-tab"
                rel="noopener noreferrer"
                icon={providerIcon}
                iconProps={providerIconProps}
              >
                View
              </Button>
            )}
          </Flex>
        ),
      });
    },
    onError() {
      void message.error("Failed to send contact");
    },
  });

  // The mapped push looks an account up before it starts, and `isSending` only covers the push
  // itself — without this the button goes live again in between and a second click pushes twice.
  const [isPreparingPush, setIsPreparingPush] = useState(false);

  // The mapping and the object's schema are both read over the network, and between them they
  // decide whether the click can push at all. Busy until both land: a contact that is mapped would
  // otherwise be turned away while the first is open, and — worse — a click while the second is
  // open would see no required fields, reveal the contact, and pay for a push the CRM refuses.
  const busy =
    isRevealing ||
    isRevealingMany ||
    isViewing ||
    isSending ||
    isPreparingPush ||
    (usesMapping && (isLoadingMapping || isDescribing));

  /** What the contact's values are before it is revealed. Its email and phone arrive with that. */
  const contactSeed = (pii: RevealedPii): CrmRecordSeed => ({
    firstName: contact.firstName ?? undefined,
    lastName: contact.lastName ?? undefined,
    jobTitle: contact.jobTitle ?? undefined,
    // Joined because the mapping can only point this at a text column — `jobFunction` is a string
    // field, so the picker never offers a picklist — and a contact's functions are a set with no
    // primary. Sending one of them would drop the rest on the choice of whichever sorted first.
    jobFunction: contact.jobFunction?.join(", "),
    seniority: contact.seniorityTier ?? undefined,
    email: pii.email ?? undefined,
    phone:
      pii.mobilePhoneNumbers?.[0] || pii.directPhoneNumbers?.[0] || pii.phoneNumbers?.[0] || "",
    buyerName: contact.buyer?.name || contact.account?.name || contact.oscarOrgName || undefined,
  });

  // Guarded because the click spends a credit revealing the contact before it pushes, and a
  // mapping with nothing in it would create an empty record for it.
  const hasContactMapping = Object.keys(contactMapping?.fields ?? {}).length > 0;

  /**
   * Fields the CRM's own create is refused without that this push will not carry. The modal
   * surfaces put these on the form; this row has none, so it refuses before paying for a reveal.
   *
   * Asked of the payload rather than of the mapping, because a field being mapped is not the same
   * as this contact having something to put in it: a mapped `LastName` is dropped from the push
   * like any other empty value, and Salesforce refuses a Contact without one just the same.
   *
   * Asked of `requiredOnCreate` rather than `required`: a field the CRM's own forms mark required
   * is not one its create is refused without, and withholding over one would block a push it takes.
   */
  const unfillableRequired = (() => {
    if (!contactMapping || !paragonCrm || !describedContact) return [];

    // The reveal supplies the email and the phone, so they count as present: flagging them here
    // would refuse every unrevealed contact before the values it is about to buy arrive.
    const form = buildRecordForm(
      "contact",
      contactMapping,
      contactSeed({ email: "revealed", mobilePhoneNumbers: ["revealed"] }),
      paragonCrm.lookupTargets,
    );
    const request = form.toPushRequest(form.initialData);
    const carried = new Set([
      ...Object.keys(request.fields),
      ...Object.keys(request.lookups),
      // Resolved from the company name after the form is built, so it is not in the payload yet.
      ...(form.crmFieldByKey.account ? [form.crmFieldByKey.account] : []),
    ]);

    return describedContact.entity.fields
      .filter((field) => field.requiredOnCreate && !carried.has(field.id))
      .map((field) => field.name);
  })();

  const toFieldMapping = () =>
    navigate({ ...appRoutes.integrationFieldMapping, hash: fieldMappingSectionId("contact") });

  const providerName = friendlyProviderName[provider];
  const providerIcon = providerIconKeyMap[provider];
  const providerIconProps = { color: providerIconColorMap[provider] };

  if (!providerType && !paragonCrm) return null;

  const companyName = contact.buyer?.name || contact.account?.name || contact.oscarOrgName || "";

  /**
   * The account the contact belongs to, matched on its company's name.
   *
   * Resolved here rather than by the service, which only does it for the providers it names the
   * fields for: a mapped push carries whatever the mapping asked for and nothing else. No match
   * leaves the contact unattached, which beats failing the push over it.
   */
  const resolveAccount = async (crmField: string, lookupTargets: LookupTargets) => {
    if (!companyName) return {};
    try {
      const { accounts } = await api.searchAccounts({
        searchAccountsRequest: { text: companyName, limit: 50 },
      });
      const exact = accounts.find((account) => account.name === companyName);
      return exact
        ? {
            [crmField]: { id: exact.id, entitySetName: lookupTargets.account },
          }
        : {};
    } catch {
      return {};
    }
  };

  /**
   * Pushes with the field names the company's mapping gives, which is all a Paragon install has:
   * we never see its schema. One click rather than the review form the lead surfaces show, because a
   * contact here is revealed as it is pushed and a form in between would spend that on a maybe.
   */
  const sendMapped = async (pii: RevealedPii) => {
    if (!contactMapping || !paragonCrm) return;

    setIsPreparingPush(true);
    try {
      const form = buildRecordForm(
        "contact",
        contactMapping,
        contactSeed(pii),
        paragonCrm.lookupTargets,
      );
      const request = form.toPushRequest(form.initialData);
      const accountField = form.crmFieldByKey.account;
      // Nothing here seeds a lookup field today, so the form's own are empty — kept so a seeded one
      // would be sent rather than silently dropped, with the resolved account winning its field.
      const lookups = {
        ...request.lookups,
        ...(accountField ? await resolveAccount(accountField, paragonCrm.lookupTargets) : {}),
      };

      sendContact({
        contactId: contact.id,
        cognismContactId,
        // Named for the providers the service builds the fields for; the mapping carries its own.
        contactCompanyName: companyName,
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        entityName: request.logicalName,
        fields: JSON.stringify(request.fields),
        ...(Object.keys(lookups).length > 0 ? { lookups: JSON.stringify(lookups) } : {}),
      });
    } finally {
      setIsPreparingPush(false);
    }
  };

  const send = (pii: RevealedPii) => {
    // Only when there is no native install to push through: one CRM at a time is enforced in the UI
    // alone, so both can be connected, and the service would resolve the native one either way.
    if (usesMapping) {
      void sendMapped(pii);
      return;
    }

    sendContact({
      salutation: "",
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      contactCompanyName: companyName,
      jobTitle: contact.jobTitle || "",
      jobFunction: contact.jobFunction ?? undefined,
      seniority: "",
      contactId: contact.id,
      cognismContactId,
      emailAddress: pii.email || "",
      // The CRM record has one phone field — prefer mobile (rarer, more valuable).
      phoneNumber:
        pii.mobilePhoneNumbers?.[0] || pii.directPhoneNumbers?.[0] || pii.phoneNumbers?.[0] || "",
    });
  };

  const handleSend = () => {
    if (disableContactsCta) {
      checkSubscription();
      return;
    }
    if (usesMapping) {
      if (isLoadingMapping || isDescribing) return;
      // Told apart, because one is the user's to fix and the other is not — and reporting an
      // outage as "not configured" sends them to a page that already has their mapping on it.
      // Either read failing leaves the guard below unable to answer, and this row spends a credit
      // before it pushes — so neither is something to proceed past.
      if (mappingError || describeError) {
        void message.error(
          `Could not read your ${paragonCrm?.name ?? "CRM"} field mapping. Please try again.`,
        );
        return;
      }
      if (!hasContactMapping) {
        // Taken there rather than told: this row has no form to fall back to, so a message on its
        // own leaves the user to find a settings page they have not been to yet.
        void message.info("Choose where Stotles writes a contact in your CRM, then push again.");
        void toFieldMapping();
        return;
      }
      /**
       * The click spends a credit revealing the contact before it pushes, and this row has no form
       * to collect what the mapping leaves out — so a required field nobody mapped would be paid
       * for and then refused. A Salesforce Lead is the case that bites: it insists on an employer,
       * and no application field carries one.
       */
      if (unfillableRequired.length > 0) {
        void message.info(
          `Your CRM will not create a contact without ${unfillableRequired.join(" and ")}. Map ${
            unfillableRequired.length === 1 ? "it" : "them"
          } first, then push again.`,
        );
        void toFieldMapping();
        return;
      }
    }
    logEvent(EventNames.sendToCRMClicked, trackingAttributes);

    // Already revealed, either source — PII is on the row.
    if (contact.tier === ContactTier.Redeemed) {
      send(contact);
      return;
    }
    const onError = () => void message.error("Failed to send contact");
    // Oscar reveal-on-push: no redeemId, so it goes through the source-tagged bulk mutation as a
    // batch of one, and the item's contact carries the channels to push.
    if (!isCognism) {
      revealMany(
        { input: { contacts: [{ source: CognismContactSource.Oscar, id: contact.id }] } },
        {
          onSuccess: (data) => {
            const revealed = data.revealContacts.perItem[0]?.contact;
            if (revealed) send(revealed);
            else onError();
          },
          onError,
        },
      );
      return;
    }
    // Previously redeemed by this company — free re-fetch.
    if (contact.isRevealed) {
      view(
        { input: { cognismContactId: contact.id } },
        { onSuccess: (data) => send(data.viewContact.contact), onError },
      );
      return;
    }
    // First-time reveal (redeem-on-push). A Cognism row with no redeemId can't be revealed.
    if (!contact.redeemId) {
      onError();
      return;
    }
    reveal(
      { input: { redeemId: contact.redeemId } },
      { onSuccess: (data) => send(data.revealContact.contact), onError },
    );
  };

  // Already in the CRM — link straight to the record instead of re-pushing.
  if (extraContactData?.externalUrl) {
    const viewLabel = `View in ${providerName}`;
    return (
      <Tooltip content={viewLabel}>
        <Button
          as="a"
          buttonType="secondary"
          href={disableContactsCta ? "" : extraContactData.externalUrl}
          onClick={(e: React.MouseEvent) => {
            if (disableContactsCta) {
              e.preventDefault();
              checkSubscription();
            }
          }}
          target="new-tab"
          rel="noopener noreferrer"
          icon={providerIcon}
          iconProps={providerIconProps}
          aria-label={viewLabel}
        />
      </Tooltip>
    );
  }

  return (
    <PaywallPopover featureType="CONTACTS" contextSource="Cognism CRM button">
      <Tooltip content="Push to CRM">
        <Button
          buttonType="secondary"
          onClick={handleSend}
          loading={busy}
          icon={providerIcon}
          iconProps={providerIconProps}
          aria-label="Push to CRM"
        />
      </Tooltip>
    </PaywallPopover>
  );
}
